import { useEffect, useState } from 'react'
import { useSyncOnChange } from '~/hooks/use-sync-on-change'

/** Pause de saisie après laquelle une recherche s'applique (#107). */
export const SEARCH_DEBOUNCE_MS = 400

/**
 * Champ de recherche d'une liste (#107) : `search` suit la saisie, `query` est
 * ce qu'on applique — après une pause de frappe, pour ne pas lancer une
 * requête (ou un recalcul) par touche. Une par frappe saturait le throttler de
 * l'API (`ThrottlerException`) sur la liste des utilisateurs.
 *
 * Une recherche **vidée** s'applique tout de suite, et annule celle qui
 * attendait : sans ça, « Aucun résultat pour … » restait affiché champ vide, et
 * l'ancienne recherche revenait si l'on retapait aussitôt.
 *
 * `delayMs` : 400 ms par défaut, pour les listes qui interrogent l'API ; une
 * recherche purement locale peut répondre plus vite (#109).
 *
 * `onQueryChange` réagit à chaque nouvelle `query` (retour en page 1, sélection
 * vidée) pendant le rendu, cf. `useSyncOnChange` : il ne doit faire que des
 * `setState` du composant appelant.
 */
export function useListSearch(onQueryChange?: () => void, delayMs = SEARCH_DEBOUNCE_MS) {
  const [search, setSearchText] = useState('')
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (search.trim() === '') return
    const handler = setTimeout(setQuery, delayMs, search)
    return () => clearTimeout(handler)
  }, [search, delayMs])

  const setSearch = (text: string) => {
    setSearchText(text)
    if (text.trim() === '') setQuery('')
  }

  useSyncOnChange(query, () => onQueryChange?.())

  return { search, setSearch, query }
}
